import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Stepper } from '../../../../components/Stepper';
import { Tooltip } from '../../../../components/Tooltip';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { verifyPassword } from '../../../../lib/reauth';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceItemsApi, evidenceStorageLocationsApi } from '../evidenceApi';
import {
  MOVEMENT,
  MOVEMENT_LABEL,
  type EvidenceItemDetail,
  type EvidenceItemListItem,
  type EvidenceMovementType,
  type EvidenceStorageLocationOption,
} from '../evidenceTypes';
import { useStaffOptions } from '../../../human-resources/personnel/useStaff';
import { sampleMovement } from '../utils/sampleEvidence';

type Step = 'movement' | 'why';

const STEPS: Step[] = ['movement', 'why'];

const STEP_LABEL: Record<Step, string> = { movement: 'Movement', why: 'Reason and confirmation' };

const FIELD_STEP: Record<string, Step> = {
  movementType: 'movement',
  toLocationId: 'movement',
  toCustodianPersonnelId: 'movement',
  transferredTo: 'movement',
  reason: 'why',
  justification: 'why',
  password: 'why',
};

const REASONS: Record<number, string[]> = {
  [MOVEMENT.changeOfStorage]: ['Vault reorganisation', 'Storage condition', 'Change of custodian'],
  [MOVEMENT.transfer]: ['Laboratory examination', 'Presentation in court', 'Turnover to another agency'],
  [MOVEMENT.disposal]: ['Case terminated', 'Court order for disposal', 'Return to owner'],
};

/**
 * Asks for evidence to MOVE — a change of storage, or a pull-out by transfer or disposal.
 *
 * <b>Nothing moves when this is submitted.</b> The request goes to approval, and the approval
 * writes the custody line. The form says so before the tick.
 *
 * <b>The PASSWORD is a field of this form</b>, not a dialog stacked on it — one modal, never
 * stacked. It is checked through `verifyPassword` first; what travels with the request is the
 * one-time proof that check issued, which the server spends and records against the movement.
 * The password itself never leaves the confirmation call. Product call, 07 Oct 2026: "password
 * only applies when movement (pulled out/change of storage) is triggered".
 *
 * Autogenerate fills the movement and the reasons; it never fills the password.
 */
export function EvidenceMovementModal({
  item,
  onClose,
  onSaved,
}: {
  item: EvidenceItemListItem;
  onClose: () => void;
  onSaved: (saved: EvidenceItemDetail) => void;
}) {
  const { options: staff, loading: loadingStaff } = useStaffOptions(true);

  // Every item is kept somewhere — a digital item's media too — so every item can change storage.
  /*
    CHANGE OF STORAGE ONLY — product call, 08 Oct 2026. A pull-out (court, disposal, other storage)
    is the case's Stored-folder Transfer, where a court transfer names its order and judge and a
    disposal its type; the server refuses the old shapes here too.
  */
  const offered: EvidenceMovementType[] = [MOVEMENT.changeOfStorage];

  const [stepIndex, setStepIndex] = useState(0);
  const [type, setType] = useState<string>(String(offered[0]));
  const [toLocationId, setToLocationId] = useState('');
  const [toCustodianId, setToCustodianId] = useState('');
  const [transferredTo, setTransferredTo] = useState('');
  const [reason, setReason] = useState('');
  const [justification, setJustification] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [locations, setLocations] = useState<EvidenceStorageLocationOption[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    evidenceStorageLocationsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setLocations(next);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingLocations(false);
      });

    return () => controller.abort();
  }, []);

  const movement = Number(type) as EvidenceMovementType;
  const isChange = movement === MOVEMENT.changeOfStorage;
  const isTransfer = movement === MOVEMENT.transfer;

  const stepValid: Record<Step, boolean> = {
    movement: isChange ? toLocationId !== '' : isTransfer ? transferredTo.trim() !== '' : true,
    why: reason.trim() !== '' && justification.trim() !== '' && password.length > 0,
  };

  const currentStep = STEPS[Math.min(stepIndex, STEPS.length - 1)];
  const isLastStep = currentStep === 'why';
  const allValid = STEPS.every((s) => stepValid[s]);
  const canSave = allValid && confirmed && !saving;

  function missingOn(step: Step): string | null {
    if (stepValid[step]) return null;
    if (step === 'movement') {
      return isChange ? 'Choose where it is moving to.' : 'Name the office it is being transferred to.';
    }
    return 'Give the reason and justification, and confirm your password.';
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);
    setPasswordError(null);

    let proof: string;

    try {
      proof = (await verifyPassword(password)).token;
    } catch {
      // The sign-in form's generic answer — nothing about the account's state.
      setPasswordError('That password is not correct.');
      setSaving(false);
      return;
    } finally {
      // Dropped as soon as it has served its purpose — what travels on is the proof.
      setPassword('');
    }

    try {
      const saved = await evidenceItemsApi.requestMovement(
        item.id,
        {
          movementType: movement,
          toLocationId: isChange ? toLocationId : null,
          toCustodianPersonnelId: isChange && toCustodianId !== '' ? toCustodianId : null,
          transferredTo: isTransfer ? transferredTo.trim() : null,
          reason: reason.trim(),
          justification: justification.trim(),
        },
        proof,
      );

      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not request the movement.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: 'evidence-movement',
        // A RadioGroup puts its id on each OPTION, so the field maps to the chosen one.
        fieldIds: { movementType: `evidence-movement-movement-type-${type}` },
        stepOf: (field) => {
          const owner = FIELD_STEP[field];
          return owner ? STEPS.indexOf(owner) : undefined;
        },
        onStep: setStepIndex,
      });
    } finally {
      setSaving(false);
    }
  }

  const CONTENT: Record<Step, ReactNode> = {
    movement: (
      <div className="flex flex-col gap-4">
        <RadioGroup
          id="evidence-movement-movement-type"
          label="Movement"
          required
          value={type}
          onChange={setType}
          disabled={saving}
          options={offered.map((value) => ({
            value: String(value),
            label: MOVEMENT_LABEL[value],
            description:
              value === MOVEMENT.changeOfStorage
                ? 'Moved to another storage location, optionally to another custodian.'
                : value === MOVEMENT.transfer
                  ? 'Pulled out to another office. It can be stored in again when it comes back.'
                  : 'Pulled out for good. Nothing further can be recorded against it.',
          }))}
          error={error?.fieldError('movementType')}
        />

        {isChange ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              id="evidence-movement-to-location-id"
              label="Move to"
              required
              placeholder="Choose a location"
              value={toLocationId}
              onChange={setToLocationId}
              options={locations
                .filter((l) => l.id !== item.storageLocationId)
                .map((l) => ({ value: l.id, label: l.name, sublabel: l.code, keywords: l.code }))}
              remote
              loading={loadingLocations}
              disabled={saving}
              error={error?.fieldError('toLocationId')}
            />

            <Select
              id="evidence-movement-to-custodian-personnel-id"
              label="New custodian"
              description="Leave empty to keep the custodian who holds it now."
              placeholder="Keep the current custodian"
              value={toCustodianId}
              onChange={setToCustodianId}
              options={staff}
              remote
              loading={loadingStaff}
              disabled={saving}
              error={error?.fieldError('toCustodianPersonnelId')}
            />
          </div>
        ) : null}

        {isTransfer ? (
          <TextField
            id="evidence-movement-transferred-to"
            label="Transferred to"
            required
            description="The office, court or agency receiving it — an organisation, not a person."
            value={transferredTo}
            onChange={(e) => setTransferredTo(e.target.value)}
            maxLength={200}
            disabled={saving}
            error={error?.fieldError('transferredTo')}
          />
        ) : null}
      </div>
    ),

    why: (
      <div className="flex flex-col gap-4">
        <Select
          id="evidence-movement-reason"
          label="Reason"
          required
          description="Why it is moving. Pick one, or type your own."
          placeholder="Choose or type a reason"
          value={reason}
          onChange={setReason}
          allowCustom
          options={(REASONS[movement] ?? []).map((r) => ({ value: r, label: r }))}
          disabled={saving}
          error={error?.fieldError('reason')}
        />

        <Textarea
          id="evidence-movement-justification"
          label="Justification"
          required
          description="The authority for the movement — the order, the request, the memo — written as it will stand on the chain of custody."
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          maxLength={2000}
          disabled={saving}
          error={error?.fieldError('justification')}
        />

        <TextField
          id="evidence-movement-password"
          label="Your password"
          required
          type="password"
          autoComplete="current-password"
          description="Confirms it is still you at this machine. It is checked and discarded, never stored; the request carries a one-time confirmation instead."
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (passwordError) setPasswordError(null);
          }}
          disabled={saving}
          error={passwordError ?? error?.fieldError('password')}
        />

        <p className="text-xs text-slate-500">
          Nothing moves when you submit. The request goes for approval; the item moves, and its
          chain of custody records the movement, only when it is approved.
        </p>
      </div>
    ),
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Move ${item.inventoryNumber}`}
      size="lg"
      id="evidence-movement-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleMovement({
            staffIds: staff.map((s) => s.value),
            locationIds: locations.filter((l) => l.id !== item.storageLocationId).map((l) => l.id),
          });

          if (isChange) {
            setToLocationId(sample.toLocationId);
            setToCustodianId(sample.toCustodianPersonnelId);
          }

          if (isTransfer) setTransferredTo(sample.transferredTo);
          setReason(sample.reason);
          setJustification(sample.justification);
          // Never the password.
        },
        disabled: saving || loadingLocations,
      }}
      confirm={isLastStep ? { id: 'evidence-movement-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined}
      footer={
        <>
          <Button id="evidence-movement-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {stepIndex > 0 ? (
            <Button
              id="evidence-movement-back"
              variant="secondary"
              onClick={() => setStepIndex((s) => Math.max(0, s - 1))}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Tooltip disabledReason={missingOn(currentStep)}>
              <Button
                id="evidence-movement-next"
                variant="secondary"
                onClick={() => setStepIndex((s) => Math.min(STEPS.length - 1, s + 1))}
                disabled={!stepValid[currentStep]}
              >
                Next
              </Button>
            </Tooltip>
          ) : null}

          <Tooltip disabledReason={allValid ? null : missingOn(STEPS.find((s) => !stepValid[s]) ?? currentStep)}>
            <Button
              id="evidence-movement-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Stepper
          id="evidence-movement"
          steps={STEPS.map((key) => ({ key, label: STEP_LABEL[key] }))}
          activeIndex={stepIndex}
          onJump={(index) => {
            if (index <= stepIndex || STEPS.slice(0, index).every((s) => stepValid[s])) setStepIndex(index);
          }}
        />

        <div id={`evidence-movement-panel-${currentStep}`} role="tabpanel" aria-labelledby={`evidence-movement-step-${currentStep}`}>
          {CONTENT[currentStep]}
        </div>
      </div>
    </Modal>
  );
}
